import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { abyssColors } from '@/theme/colors';
import { GroupedVersions } from '@/types';
import { Close, DeleteSweep, Restore, SmartToy, UploadFile } from '@mui/icons-material';
import { Avatar, IconButton, Stack, Typography, alpha } from '@mui/material';
import { GetVersionsDocument, Version, useRollbackVersionMutation } from '@/__generated__/graphql';
import { TimeLine, VersionContainer } from './styles';
import { VersionUpdatesModal } from './VersionUpdatesModal';
import { useMemo, useState } from 'react';
import { RestoreConfirmationDialog } from './RestoreConfirmationDialog';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { NOT_APPLICABLE_LABEL, RESTORED_VERSION, SUPPORT_ERROR_MESSAGE } from '@/constants';
import { DocumentNode } from '@apollo/client';
import { convertTimeStampToDate } from '@/utils';
import { useAtom } from 'jotai';
import { snackbarMessage as analysisSnackbarMessageAtom } from '@/components/Analysis/state';
import { Snackbar } from '../Snackbar';
import { Pill } from '@/components/shared/Pill';
import { PillStatus } from '../Pill/types';

const getStatus = (sourceText: string): PillStatus => {
  switch (sourceText) {
    case 'POF calculation':
    case 'POF - Legacy Values Cleared':
      return 'linked';
    case 'COF calculation':
    case 'COF - Legacy Values Cleared':
      return 'failed';
    case 'Transition period':
    case 'Transition Period - Legacy Values Cleared':
      return 'warning';
    case 'Version Restore':
      return 'linked';
    default:
      return 'info';
  }
};

const getPillIcon = ({
  isLegacyValuesCleared,
  isRestored,
  isEsUpload,
}: {
  isLegacyValuesCleared: boolean;
  isRestored: boolean;
  isEsUpload: boolean;
}) => {
  if (isRestored) {
    return <Restore sx={{ fontSize: '1.4rem' }} />;
  }
  if (isLegacyValuesCleared) {
    return <DeleteSweep sx={{ fontSize: '1.4rem' }} />;
  }
  if (isEsUpload) {
    return <UploadFile sx={{ fontSize: '1.4rem' }} />;
  }
  return undefined;
};

const ChangeSourcePill = ({
  source,
  isRestored,
  isLegacyValuesCleared,
  isEsUpload,
}: {
  source: string;
  isRestored: boolean;
  isLegacyValuesCleared: boolean;
  isEsUpload: boolean;
}) => {
  const status = getStatus(source);
  const icon = getPillIcon({ isLegacyValuesCleared, isRestored, isEsUpload });
  return (
    <Pill
      label={source}
      status={status}
      size="medium"
      state="read"
      icon={icon}
      showIcon={!!icon}
      disabled={!!icon}
    />
  );
};

const VersionListItem = ({
  version,
  handleClick,
}: {
  version: Version;
  handleClick: () => void;
}) => {
  const creationTime = convertTimeStampToDate(version.createdAt ?? '')?.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  const isRestored = version.lastModifiedFor === RESTORED_VERSION;
  const isLegacyValuesCleared = version.lastModifiedFor?.includes('Legacy Values Cleared');
  const isEsUpload = version.lastModifiedFor === 'ES Sheet Upload';

  return (
    <Stack
      mb={3}
      sx={{ cursor: isRestored || isLegacyValuesCleared || isEsUpload ? 'default' : 'pointer' }}
      onClick={handleClick}
    >
      <Stack direction="row" alignItems="center" gap={2}>
        <AbyssIcon iconName="version-item" style={{ width: '8px', height: '8px' }} />
        <Stack direction="row" alignItems="center" gap={1}>
          <Avatar sx={{ width: '24px', height: '24px' }}>
            {version.lastModifiedByUser?.picture && version.lastModifiedBy !== 'CLI' && (
              <img width="24px" height="24px" src={version.lastModifiedByUser?.picture ?? ''} />
            )}
            {!version.lastModifiedByUser?.picture && version.lastModifiedBy === 'CLI' && (
              <SmartToy sx={{ fontSize: '1.6rem' }} />
            )}
          </Avatar>

          <Typography fontSize="1.4rem" fontWeight={600} color={abyssColors.primary[500]}>
            {version.lastModifiedByUser?.name ?? 'Abyss Bot'}
          </Typography>
        </Stack>
      </Stack>
      <Stack ml={7} mt={0.5} gap={1}>
        <Typography fontSize="1.2rem" color={alpha('#000', 0.3)}>
          {creationTime?.toLowerCase() ?? NOT_APPLICABLE_LABEL}
        </Typography>
        <Stack direction="row" alignItems="center" gap="0.8rem">
          <ChangeSourcePill
            source={version.lastModifiedFor}
            isRestored={isRestored}
            isLegacyValuesCleared={isLegacyValuesCleared}
            isEsUpload={isEsUpload}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

const VersionDay = ({
  date,
  groupedVersions,
  setVersion,
}: {
  date: string;
  groupedVersions: GroupedVersions;
  setVersion: (argument0: Version) => void;
}) => {
  const restrictedVersions = new Set(['Initialization', 'Version Restore']);
  const restrictedESVersions = new Set([
    'POF - Legacy Values Cleared',
    'COF - Legacy Values Cleared',
    'Transition Period - Legacy Values Cleared',
    'ES Sheet Upload',
  ]);
  return (
    <Stack key={date} mb={3} position="relative">
      <TimeLine />
      <Stack direction="row" alignItems="center" gap="12px" mb={4}>
        <AbyssIcon iconName="version-day" style={{ height: '8px', width: '8px' }} />
        <Typography fontSize="1.2rem" color={abyssColors.primary[400]}>
          {date}
        </Typography>
      </Stack>
      {groupedVersions[date].map((version) => (
        <VersionListItem
          key={version.id}
          version={version}
          handleClick={() => {
            if (
              !restrictedVersions.has(version.lastModifiedFor) &&
              !restrictedESVersions.has(version.lastModifiedFor)
            ) {
              setVersion(version);
            }
          }}
        />
      ))}
    </Stack>
  );
};

export const VersionsList = ({
  days,
  groupedVersions,
  onClose,
  disableClose,
  allowRestore,
  refetchDocuments = [],
  isESVersion = false,
}: {
  days: string[];
  groupedVersions: GroupedVersions;
  onClose: () => void;
  disableClose?: boolean;
  allowRestore?: boolean;
  refetchDocuments?: DocumentNode[];
  isESVersion?: boolean;
}) => {
  const [selectedVersion, setSelectedVersion] = useState<Version | undefined>(undefined);
  const [showRestoreDialog, setshowRestoreDialog] = useState<boolean>(false);
  const [rollback, { loading }] = useRollbackVersionMutation();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [snackbarMessage, setSnackbarMessage] = useAtom(analysisSnackbarMessageAtom);

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };

  const { shouldShow, content } = snackbarMessage;

  const restoreVersion = async () => {
    if (selectedVersion) {
      try {
        const response = await rollback({
          variables: {
            input: {
              structureId: selectedVersion?.structureId ?? '',
              rollbackVersionId: selectedVersion?.id ?? '',
              referenceId: selectedVersion?.referenceId ?? '',
            },
          },
          refetchQueries: [GetVersionsDocument, ...refetchDocuments],
        });
        if (response.data?.rollbackVersion) {
          showSnackBar('Version restored', 'success');
          setshowRestoreDialog(false);
          setSelectedVersion(undefined);
        } else {
          showSnackBar(SUPPORT_ERROR_MESSAGE, 'error');
        }
      } catch {
        showSnackBar(SUPPORT_ERROR_MESSAGE, 'error');
      }
    }
  };

  const latestVersion = useMemo(() => {
    const versions = Object.values(groupedVersions)
      .flat()
      .sort(
        (a, b) =>
          (convertTimeStampToDate(b.createdAt ?? '')?.getTime() || 0) -
          (convertTimeStampToDate(a.createdAt ?? '')?.getTime() || 0)
      );
    return versions.at(0);
  }, [groupedVersions]);

  return (
    <>
      <Snackbar open={shouldShow} onClose={closeSnack}>
        {content}
      </Snackbar>
      <VersionContainer padding={2} gap={4}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontWeight={700} color={abyssColors.primary[500]}>
            Version History
          </Typography>
          {!disableClose && (
            <IconButton onClick={onClose} sx={{ padding: 0 }} disableRipple>
              <Close sx={{ color: abyssColors.primary[500], width: '2rem', height: '2rem' }} />
            </IconButton>
          )}
        </Stack>
        <Stack>
          {days.map((day) => (
            <VersionDay
              key={day}
              date={day}
              groupedVersions={groupedVersions}
              setVersion={setSelectedVersion}
            />
          ))}
        </Stack>
      </VersionContainer>
      {!showRestoreDialog && (
        <VersionUpdatesModal
          open={!!selectedVersion}
          handleClose={() => {
            setSelectedVersion(undefined);
          }}
          updatedData={selectedVersion?.updatedData ?? {}}
          title={selectedVersion?.lastModifiedFor ?? ''}
          showOnlyLastLabel
          allowRestore={latestVersion?.id === selectedVersion?.id ? false : allowRestore}
          handleRestoreClick={() => {
            setshowRestoreDialog(true);
          }}
          isESVersion={isESVersion}
        />
      )}
      <RestoreConfirmationDialog
        open={showRestoreDialog}
        handleClose={() => {
          setshowRestoreDialog(false);
        }}
        handleConfirm={restoreVersion}
        loading={loading}
      />
    </>
  );
};
